import React, { useState, useEffect, useRef, useContext, useCallback, useMemo } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Avatar from '@mui/material/Avatar';
import IconButton from '@mui/material/IconButton';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import SaveIcon from '@mui/icons-material/Save';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import api from '../services/api';
import { AuthContext } from '../contexts/AuthContext';
import AlertModal from '../components/AlertModal';
import PageContainer from '../components/ui/PageContainer';
import AvatarCropDialog from '../components/ui/AvatarCropDialog';
import SecuritySettings from '../components/SecuritySettings';
import { User as UserType, AlertState } from '../types';
import { getUserPhotoUrl } from '../utils/userPhoto';

const PROFILE_PHOTO_MAX_BYTES = 4 * 1024 * 1024;

/** Desenvolvedor: apenas edição do próprio perfil (nome, e-mail, senha, foto). */
const DeveloperSelfProfile: React.FC = () => {
  const authContext = useContext(AuthContext);
  const { user, refreshUser } = authContext || {};
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [cropOpen, setCropOpen] = useState(false);
  const [alert, setAlert] = useState<AlertState>({ isOpen: false, message: '', type: 'error' });
  const photoFileInputRef = useRef<HTMLInputElement | null>(null);

  const loadSelf = useCallback(async (): Promise<void> => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get<UserType>(`/users/${user.id}`);
      setName(data.name);
      setEmail(data.email);
      setPhotoFile(null);
      setPhotoPreview(getUserPhotoUrl(data));
    } catch (e) {
      console.error(e);
      setAlert({ isOpen: true, message: 'Não foi possível carregar seu perfil.', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    void loadSelf();
  }, [loadSelf]);

  // Step 1: user picks a file → open the crop dialog with the raw image.
  const handlePhotoChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    event.target.value = ''; // allow re-selecting the same file later
    if (!file) return;
    if (file.size > PROFILE_PHOTO_MAX_BYTES) {
      setAlert({ isOpen: true, message: 'A imagem deve ter no máximo 4 MB.', type: 'warning' });
      return;
    }
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    setCropSrc(URL.createObjectURL(file));
    setCropOpen(true);
  };

  // Step 2: crop confirmed → use the cropped square as the pending photo.
  const handleCropConfirm = (cropped: File): void => {
    setCropOpen(false);
    if (cropSrc) {
      URL.revokeObjectURL(cropSrc);
      setCropSrc(null);
    }
    if (photoPreview?.startsWith('blob:')) URL.revokeObjectURL(photoPreview);
    setPhotoFile(cropped);
    setPhotoPreview(URL.createObjectURL(cropped));
  };

  const handleCropCancel = (): void => {
    setCropOpen(false);
    if (cropSrc) {
      URL.revokeObjectURL(cropSrc);
      setCropSrc(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!user?.id) return;

    const payload = new FormData();
    payload.append('name', name);
    payload.append('email', email);
    if (photoFile) {
      payload.append('photo', photoFile);
    }

    try {
      await api.patch('/users/me', payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (refreshUser) await refreshUser();
      await loadSelf();
      setAlert({ isOpen: true, message: 'Perfil atualizado com sucesso!', type: 'success' });
    } catch (error: any) {
      setAlert({
        isOpen: true,
        message: error.response?.data?.message || 'Erro ao atualizar perfil',
        type: 'error',
      });
    }
  };

  const initials = useMemo(
    () =>
      (name || user?.name || '')
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((p) => p[0]?.toUpperCase())
        .join('') || '?',
    [name, user?.name],
  );

  if (loading) {
    return (
      <PageContainer title="Meu perfil" subtitle="Buscando seus dados…">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer
      icon={<PersonOutlinedIcon />}
      maxWidth="xl"
      title="Meu perfil"
      subtitle="Atualize nome, e-mail, senha e foto. Demais dados (cargo, gestor, competências) são mantidos pelo RH ou administração."
    >
      <Paper component="form" onSubmit={(e) => void handleSubmit(e)} sx={{ p: { xs: 2, sm: 3 } }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <Typography variant="subtitle2" component="span" sx={{ display: 'block', mb: 1 }}>
              Foto do perfil{' '}
              <Typography component="small" variant="caption" color="text.secondary">
                (máx. 4 MB · quadrada 1:1)
              </Typography>
            </Typography>
            <input
              ref={photoFileInputRef}
              type="file"
              accept="image/*"
              onChange={handlePhotoChange}
              tabIndex={-1}
              aria-hidden="true"
              style={{
                position: 'absolute',
                width: 1,
                height: 1,
                padding: 0,
                margin: -1,
                overflow: 'hidden',
                clip: 'rect(0 0 0 0)',
                whiteSpace: 'nowrap',
                border: 0,
              }}
            />
            <Stack direction="row" spacing={2.5} sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              {/* Round avatar preview with a camera overlay button */}
              <Box sx={{ position: 'relative', width: 96, height: 96 }}>
                <Avatar
                  src={photoPreview || undefined}
                  sx={{ width: 96, height: 96, fontSize: '2rem', fontWeight: 700, bgcolor: 'primary.main' }}
                >
                  {initials}
                </Avatar>
                <IconButton
                  type="button"
                  aria-label="Escolher e ajustar foto do perfil"
                  onClick={() => photoFileInputRef.current?.click()}
                  sx={{
                    position: 'absolute',
                    right: -4,
                    bottom: -4,
                    width: 34,
                    height: 34,
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    border: '2px solid',
                    borderColor: 'background.paper',
                    '&:hover': { bgcolor: 'primary.dark' },
                    '& svg': { fontSize: 18 },
                  }}
                >
                  <PhotoCameraOutlinedIcon />
                </IconButton>
              </Box>
              <Box>
                <Button
                  type="button"
                  variant="outlined"
                  startIcon={<PhotoCameraOutlinedIcon />}
                  onClick={() => photoFileInputRef.current?.click()}
                >
                  {photoPreview ? 'Trocar foto' : 'Adicionar foto'}
                </Button>
                {photoFile ? (
                  <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 0.75, fontWeight: 700 }}>
                    Nova foto pronta — salve para aplicar.
                  </Typography>
                ) : (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                    Você poderá recortar e dar zoom após escolher.
                  </Typography>
                )}
              </Box>
            </Stack>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Divider sx={{ my: 1 }} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              id="dev-name"
              label="Nome"
              fullWidth
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              slotProps={{ htmlInput: { minLength: 2 } }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              id="dev-email"
              label="E-mail"
              type="email"
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Stack direction="row" sx={{ justifyContent: 'flex-end' }}>
              <Button type="submit" variant="contained" startIcon={<SaveIcon />}>
                Salvar alterações
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      <Box sx={{ mt: { xs: 2, md: 3 } }}>
        <SecuritySettings />
      </Box>

      <AvatarCropDialog
        open={cropOpen}
        imageSrc={cropSrc}
        onCancel={handleCropCancel}
        onConfirm={handleCropConfirm}
      />

      <AlertModal
        isOpen={alert.isOpen}
        onClose={() => setAlert({ ...alert, isOpen: false })}
        message={alert.message}
        type={alert.type}
      />
    </PageContainer>
  );
};

export default DeveloperSelfProfile;
